import React, { useState, useEffect, useRef } from 'react';
import { Platform, DeviceEventEmitter } from 'react-native';
import { BACKEND_URL } from '../Config/Const';
import PlatformDetector from '../utils/PlatformDetector';
import Storage from '../utils/Storage';

/**
 * Componente padre que obtiene todos los banners de la API y los distribuye
 * a los componentes correspondientes según el valor de 'fila'.
 */
const BannerManager = ({ children }) => {
  const [banners, setBanners] = useState({
    fila1: [], // BannerBotonesHeader
    fila2: [], // BannerPrincipal
    fila3: [], // BannerBancario
    fila4: [], // BannersDuales (primera posición)
    fila5: [], // BannersDuales (segunda posición)
    fila6: [], // BannerOfertasEspeciales
    fila7: [], // BannerInferior
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const selectedCityIdRef = useRef(null);

  // Determinar si estamos en web o móvil para usar la imagen adecuada
  const isWeb = PlatformDetector.isWeb();

  // Referencia para controlar la frecuencia de llamadas y reintentos
  const fetchRef = useRef({ loading: false, lastFetch: 0, retryCount: 0, mounted: true });
  const rawBannersRef = useRef([]);
  const RETRY_DELAY = 10000; // 10 segundos entre reintentos
  const MAX_RETRIES = 2;     // Máximo 2 reintentos

  const organizeBanners = (bannersData, cityId) => {
    const organizedBanners = {
      fila1: [],
      fila2: [],
      fila3: [],
      fila4: [],
      fila5: [],
      fila6: [],
      fila7: [],
    };

    const normalizedCityId = cityId === null || cityId === undefined || cityId === ''
      ? null
      : Number(cityId);

    const filteredByCity = Array.isArray(bannersData)
      ? bannersData.filter((banner) => {
          const bannerCity = banner?.ciudad;
          const normalizedBannerCity = bannerCity === null || bannerCity === undefined || bannerCity === ''
            ? 0
            : Number(bannerCity);

          // Sin ciudad seleccionada: solo mostrar banners con ciudad = 0
          if (!normalizedCityId) {
            return normalizedBannerCity === 0;
          }
          
          // Con ciudad seleccionada: mostrar ciudad = 0 O ciudad = [seleccionada]
          if (normalizedBannerCity === 0) return true;
          return normalizedBannerCity === normalizedCityId;
        })
      : [];

    filteredByCity.forEach(banner => {
      const filaKey = `fila${banner.fila}`;
      if (organizedBanners.hasOwnProperty(filaKey)) {
        const processedBanner = {
          ...banner,
          titulo: banner.titulo || '',
          imagenWeb: banner.imagenWeb ?
            (banner.imagenWeb.startsWith('http') ? banner.imagenWeb : `${BACKEND_URL}${banner.imagenWeb}`) :
            null,
          imagenMobile: banner.imagenMobile ?
            (banner.imagenMobile.startsWith('http') ? banner.imagenMobile : `${BACKEND_URL}${banner.imagenMobile}`) :
            null,
          imagenCucarda: banner.imagenCucarda ?
            (banner.imagenCucarda.startsWith('http') ? banner.imagenCucarda : `${BACKEND_URL}${banner.imagenCucarda}`) :
            null,
        };

        if (filaKey === 'fila1' || filaKey === 'fila6') {
          organizedBanners[filaKey].push(processedBanner);
        } else {
          const isWebPlatform = PlatformDetector.isWeb();
          if ((isWebPlatform && processedBanner.imagenWeb) ||
              (!isWebPlatform && (processedBanner.imagenMobile || processedBanner.imagenWeb))) {
            organizedBanners[filaKey].push(processedBanner);
          }
        }
      }
    });

    Object.keys(organizedBanners).forEach(key => {
      organizedBanners[key].sort((a, b) => a.posicion - b.posicion);
    });

    return organizedBanners;
  };

  const loadSelectedCityId = async () => {
    try {
      const storedCity = await Storage.getItem('ciudad');
      const normalized = storedCity === null || storedCity === undefined || storedCity === ''
        ? null
        : Number(storedCity);
      const next = Number.isNaN(normalized) ? null : normalized;
      selectedCityIdRef.current = next;
      setSelectedCityId(next);
    } catch (e) {
      selectedCityIdRef.current = null;
      setSelectedCityId(null);
    }
  };

  useEffect(() => {
    loadSelectedCityId();

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleCustomStorageChange = (e) => {
        if (e?.detail?.key === 'ciudad' || e?.detail?.key === 'ciudadNom' || e?.detail?.key === 'sucursalId') {
          loadSelectedCityId();
        }
      };

      window.addEventListener('localStorageChange', handleCustomStorageChange);
      return () => {
        window.removeEventListener('localStorageChange', handleCustomStorageChange);
      };
    }

    const sub = DeviceEventEmitter?.addListener?.('sucursalChanged', () => {
      loadSelectedCityId();
    });

    return () => {
      if (sub?.remove) sub.remove();
    };
  }, []);

  useEffect(() => {
    selectedCityIdRef.current = selectedCityId;
  }, [selectedCityId]);

  useEffect(() => {
    if (rawBannersRef.current && rawBannersRef.current.length > 0) {
      setBanners(organizeBanners(rawBannersRef.current, selectedCityId));
    }
  }, [selectedCityId]);

  useEffect(() => {
    // Marcar como montado
    fetchRef.current.mounted = true;
    
    const fetchBanners = async () => {
      // Evitar ejecutar si el componente ya no está montado
      if (!fetchRef.current.mounted) return;
      
      // Evitar llamadas múltiples si ya está cargando
      if (fetchRef.current.loading) return;
      
      const now = Date.now();
      // Si ha pasado muy poco tiempo desde la última llamada, salimos
      if (now - fetchRef.current.lastFetch < 2000) {
        return;
      }
      
      fetchRef.current.loading = true;
      fetchRef.current.lastFetch = now;
      
      try {
        setLoading(true);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 segundos timeout
        
        const response = await fetch(`${BACKEND_URL}/rest/productos/banner`, {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`Error al obtener banners: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!fetchRef.current.mounted) return; // Verificar nuevamente si el componente sigue montado
        
        if (data?.dsResultado?.resultado?.[0]?.resultado) {
          // Reiniciar contador de reintentos cuando tenemos éxito
          fetchRef.current.retryCount = 0;
          
          const bannersData = data.dsResultado.banners || [];
          rawBannersRef.current = bannersData;
          setBanners(organizeBanners(bannersData, selectedCityIdRef.current));
        } else {
          throw new Error('No se pudieron obtener los banners');
        }
      } catch (err) {
        // No mostrar errores de abort que son normales cuando el componente se desmonta
        if (err.name !== 'AbortError') {
          console.error('Error al cargar banners:', err);
          setError(err.message);
          
          // Lógica de reintentos limitados
          if (fetchRef.current.mounted && fetchRef.current.retryCount < MAX_RETRIES) {
            fetchRef.current.retryCount++;
            
            // Programar un reintento con delay
            setTimeout(() => {
              if (fetchRef.current.mounted) {
                fetchRef.current.loading = false;
                fetchBanners();
              }
            }, RETRY_DELAY);
          }
        }
      } finally {
        // Solo modificar el estado si el componente sigue montado
        if (fetchRef.current.mounted) {
          setLoading(false);
          // Liberar el bloqueo después de un tiempo
          setTimeout(() => {
            if (fetchRef.current.mounted) {
              fetchRef.current.loading = false;
            }
          }, 1000);
        }
      }
    };

    // Iniciar la carga de banners
    fetchBanners();
    
    // Limpieza para evitar memory leaks y actualizaciones en componentes desmontados
    return () => {
      fetchRef.current.mounted = false;
    };
  }, []);

  // Crear un contexto con los banners organizados para que los componentes hijos puedan acceder
  const bannerContext = {
    banners,
    loading,
    error,
    isWeb,
  };

  // Renderizar los hijos con el contexto de banners
  return (
    <BannerContext.Provider value={bannerContext}>
      {children}
    </BannerContext.Provider>
  );
};

// Crear un contexto para compartir los banners con los componentes hijos
export const BannerContext = React.createContext({
  banners: {
    fila1: [],
    fila2: [],
    fila3: [],
    fila4: [],
    fila5: [],
    fila6: [],
    fila7: [],
  },
  loading: true,
  error: null,
  isWeb: false,
});

// Hook personalizado para facilitar el acceso al contexto
export const useBanners = () => React.useContext(BannerContext);

export default BannerManager;
